import { useLayoutEffect, useRef } from 'react';
import { MediaClock } from '../../frame-sequence-player';

/**
 * Drive an element's position from the playback clock at 60fps. While `active`,
 * runs a requestAnimationFrame loop that computes the playhead's percentage across
 * [start, end] and hands it to `apply`, which sets `left`/visibility imperatively —
 * so this never triggers a React render and stays glued to the frames (no 100ms
 * step, no CSS transition). Seeds before first paint and cancels on unmount.
 *
 * Shared by the main timeline seeker and the per-camera media-box seeker; `apply`
 * is read from a ref so re-renders don't restart the loop.
 */
export function useClockPosition(
  clock: MediaClock | undefined,
  start: number,
  end: number,
  active: boolean,
  apply: (pct: number) => void
): void {
  const applyRef = useRef(apply);
  applyRef.current = apply;

  useLayoutEffect(() => {
    if (!clock || !active) return;
    const compute = () => {
      const span = end - start;
      applyRef.current(span > 0 ? ((clock.getTime() - start) / span) * 100 : 0);
    };
    compute(); // seed before first paint
    let raf = requestAnimationFrame(function tick() {
      compute();
      raf = requestAnimationFrame(tick);
    });
    return () => cancelAnimationFrame(raf);
  }, [clock, start, end, active]);
}
